import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Alert, Spinner, ListGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, Music, User, Clock } from 'react-feather';
import { getEvent, getPublicRequests } from '../services/api';
import RequestForm from '../components/RequestForm';
import {SongRequest} from "../types/SongRequest";
import {Event} from "../types/Event";

const RequestPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();
  const [event, setEvent] = useState<Event | null>(null);
  const [requests, setRequests] = useState<SongRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        setError('');

        if (!eventId) {
          setError('No event ID provided');
          setLoading(false);
          return;
        }

        // Fetch event details
        const requestResponse = await getPublicRequests(eventId);
        setRequests(requestResponse.data);

        const eventResponse = await getEvent(eventId);
        setEvent(eventResponse.data);

        // Set DJ data if available
        if (requestResponse.data.dj) {
          setEvent(prev => ({
            ...prev!,
            dj: requestResponse.data.dj
          }));
        }

        // Set approved requests

      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to load event');
        console.error('Error fetching event data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId]);

  const handleRequestSubmitted = async (newRequest: SongRequest) => {
    setRequests(requests => [newRequest, ...requests]);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(navigator.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);

    if (diffMins < 1) return t('requestPage.justNow');
    if (diffMins < 60) return t('requestPage.minutesAgo', { count: diffMins });

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('requestPage.hoursAgo', { count: diffHours });

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return t('requestPage.daysAgo', { count: diffDays });

    const diffMonths = Math.floor(diffDays / 30);
    return t('requestPage.monthsAgo', { count: diffMonths });
  };

  if (loading) {
    return (
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{t('requestPage.loading')}</p>
        </Container>
    );
  }

  if (error || !event) {
    return (
        <Container className="py-5">
          <Alert variant="danger">
            <Alert.Heading>{t('requestPage.error.title')}</Alert.Heading>
            <p>{error || t('requestPage.error.message')}</p>
            <Link to="/" className="btn btn-outline-danger">
              {t('requestPage.error.goBack')}
            </Link>
          </Alert>
        </Container>
    );
  }

  return (
      <Container className="py-5">
        <Row>
          {/* Request Form - Will appear first on mobile */}
          <Col lg={4} className="order-lg-2 mb-4">
            <RequestForm
                eventId={event.id}
                eventName={event.name}
                djName={event.dj?.name || 'DJ'}
                onRequestSubmitted={handleRequestSubmitted}
            />
          </Col>

          {/* Event Details and Requests - Will appear second on mobile */}
          <Col lg={8} className="order-lg-1">
            <Card className="shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex align-items-start justify-content-between mb-4">
                  <div>
                    <h1 className="h3 mb-1">{event.name}</h1>
                    {event.dj && (
                        <div className="text-muted">
                          <Link to={`/dj/${event.dj.username}`} className="text-decoration-none">
                            {t('requestPage.hostedBy')} {event.dj.name}
                            {event.dj.is_verified && (
                                <Badge bg="primary" className="ms-1" style={{ fontSize: '0.6rem' }}>
                                  ✓
                                </Badge>
                            )}
                          </Link>
                        </div>
                    )}
                  </div>
                  <Badge bg="success" className="py-2 px-3">
                    {t('requestPage.active')}
                  </Badge>
                </div>

                {event.description && (
                    <div className="mb-4">
                      <p>{event.description}</p>
                    </div>
                )}

                <div className="d-flex flex-wrap mb-2">
                  {event.location && (
                      <div className="me-4 mb-2 d-flex align-items-center text-muted">
                        <MapPin size={16} className="me-1" />
                        {event.location}
                      </div>
                  )}
                  {event.start_date && (
                      <div className="me-4 mb-2 d-flex align-items-center text-muted">
                        <Calendar size={16} className="me-1" />
                        {formatDate(event.start_date)}
                        {event.end_date && ` - ${formatDate(event.end_date)}`}
                      </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            <Card className="shadow-sm">
              <Card.Header className="bg-white py-3">
                <h2 className="h5 mb-0">{t('requestPage.approvedRequests')}</h2>
              </Card.Header>
              <ListGroup variant="flush">
                {
                  requests.map(request => (
                      <ListGroup.Item key={request.id} className="py-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="mb-1 fw-bold">
                              <Music size={16} className="me-2 text-primary" />
                              {request.song_name}
                              {request.artist_name && ` - ${request.artist_name}`}
                            </div>
                            <div className="d-flex align-items-center text-muted small mb-2">
                              <User size={14} className="me-1" />
                              {t('requestPage.requestedBy')} {request.requester_name}
                              <Clock size={14} className="ms-2 me-1" />
                              {formatTimeAgo(request.created_at)}
                            </div>
                            {request.message && <p className="mb-1 small">{request.message}</p>}
                            {typeof request.donation_amount === 'number' && (
                                <div className="small text-success">
                                  {t('requestPage.donationAmount')}: ${request.donation_amount.toFixed(2)}
                                </div>
                            )}
                            {request.payment_status && (
                                <div className="small text-info">
                                  Payment Status: {request.payment_status}
                                </div>
                            )}
                          </div>
                        </div>
                      </ListGroup.Item>
                  ))
                }
              </ListGroup>
            </Card>
          </Col>
        </Row>
      </Container>
  );
};

export default RequestPage;
